/* eslint-disable no-loop-func */
import "../MuseoSansBinary.js"; // import the custom font binary
import { upperFirst } from "lodash";
import { createLetterPdf } from "../pdfUtils";
import { getRelayLegLabel } from "./relayHelpers";

function getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight) {
    if (!imageWidth || !imageHeight) {
        return { width: maxWidth, height: maxHeight };
    }

    const widthRatio = maxWidth / imageWidth;
    const heightRatio = maxHeight / imageHeight;
    const scale = Math.min(widthRatio, heightRatio, 1);

    return {
        width: imageWidth * scale,
        height: imageHeight * scale,
    };
}

function drawReportLogo(doc, logoDataUri, imageWidth, imageHeight, x, y, maxWidth, maxHeight) {
    if (!logoDataUri) return;

    try {
        const size = getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight);
        const drawX = x + ((maxWidth - size.width) / 2);
        const drawY = y + ((maxHeight - size.height) / 2);
        doc.addImage(logoDataUri, drawX, drawY, size.width, size.height);
    } catch (_err) {
        // Ignore logo rendering failures so report formatting remains stable.
    }
}

const FINALS_TIER_LABELS = [
    "A Final - Championship",
    "B Final - Consolation",
    "C Final - Bonus",
    "D Final - Bonus",
]

function generateHeatSheetPDF(meet, props, numColumns, options = {}) {
    const doc = createLetterPdf();

    //set the number of lanes for the meet
    const NUM_LANES = meet.numLanes;

    // Set the margins and calculate the width of the content area
    const margin = 5;
    const pageWidth = doc.internal.pageSize.getWidth() - 10; // Adjusting for left and right margins
    const columnWidth = (pageWidth - ((numColumns - 1) /*margin*/)) / numColumns;

    let currentColumn = 0;
    let yPos = 30;

    // Function to draw grid lines and headers on each page
    const drawGridAndHeaders = () => {
        for (let i = 1; i < numColumns; i++) {
            doc.line((i * columnWidth) + margin, 27, (i * columnWidth) + margin, 290);
        }

        // Set font size and style for meet title and date
        doc.setFontSize(12);
        doc.setFont("MuseoSansRounded-500", "normal");

        // Centering the meet title and location
        let title = meet.meetName;
        const maxTitleLength = pageWidth; // Maximum length in characters
        if (title.length > maxTitleLength) {
            title = title.substring(0, maxTitleLength - 3) + '...';
        }
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth + (2 * margin) - titleWidth) / 2;
        doc.text(title, titleX, 15);

        // Centering the meet date
        const dateWidth = doc.getTextWidth(meet.meetDate);
        const dateX = (pageWidth + (2 * margin) - dateWidth) / 2;
        doc.text(meet.meetDate, dateX, 20);

        //left center the swimminglyFan info
        doc.setFontSize(8);
        doc.text("Download SwimminglyFan",5,8);
        doc.text("(iOS / Android), paperless heat sheets & results",5,11);

    };

    // Function to check page height and add a new page or column if needed
    const checkPageHeight = (requiredSpace) => {

        // console.log("Required Space:" + requiredSpace);
        // console.log("Y Position:" + yPos);
        // console.log("Need to add new column or page:" + ((requiredSpace + yPos) > 290));
        // console.log("________________________");
        // Slightly different bottom margin for 2- vs 3-column layouts.
        const maxY = numColumns === 2 ? 280 : 280;

        if (yPos + requiredSpace > maxY) {
            yPos = 30;
            currentColumn++;
            if (currentColumn >= numColumns) {
                currentColumn = 0;
                doc.addPage();
                drawGridAndHeaders();
            }
        }
    };

    //check how many entrants are in the heat and adjust the space accordingly
    const checkRelayHeatHeight = (heat) => {
        const headerHeight = numColumns === 3 ? 3 : 4;
        // 3-row layout per filled lane: header row + gap + 2 swimmer rows
        const laneHeightWithEntrant = numColumns === 3 ? 10 : 13;
        const laneHeightEmpty = numColumns === 3 ? 3 : 4;
        const afterHeatGap = numColumns === 3 ? 2 : 3;

        const numEntrants = heat.entrants.length;
        const lanesWithEntrants = Math.min(numEntrants, NUM_LANES);
        const lanesEmpty = Math.max(NUM_LANES - lanesWithEntrants, 0);

        return (
            headerHeight +
            lanesWithEntrants * laneHeightWithEntrant +
            lanesEmpty * laneHeightEmpty +
            afterHeatGap
        );
    }
    

    // Predefine header widths outside the loop to avoid "unsafe references" warning
    // relay headers: Lane Team Relay SeedTime
    // non-relay headers: Lane Name Age Team ID SeedTime
    
    //3 column specific header widths
    const headerWidths3ColumnsRelay = [5, 5, 42, 12];
    const eventHeaders3ColumnsRelay = [5, 5, 42, 12];
    const headerWidths3Columns = [7, 23, 7, 8, 7, 12];
    const eventHeaders3Columns = [7, 23, 7, 8, 7, 12];

    //need to change these four to fix 2 column heat sheet
    //2 column specific header widths
    const headerWidths2ColumnsRelay = [7, 7, 63, 18];
    const eventHeaders2ColumnsRelay = [7, 7, 63, 18];
    const headerWidths2Columns = [10, 34, 10, 10, 10, 18];
    const eventHeaders2Columns = [10, 34, 10, 10, 10, 18];

    // Initial grid/headers drawing
    drawGridAndHeaders();

    meet.events.forEach((event, index) => {

        // Check if the event is a relay
        const isRelay = event.isRelay === 1;

        // Calculate the required space for the event title, headers, and
        // the first heat using values that mirror the actual row spacing
        // below so we don't overflow the bottom margin.
        const eventHeaderSpace = 15; // Space for the event title and headers
        const nonRelayHeaderHeight = numColumns === 3 ? 3 : 4;
        const nonRelayLaneHeight = numColumns === 3 ? 3 : 4;
        const nonRelayAfterHeatGap = numColumns === 3 ? 2 : 3;

        const estimatedHeatHeight = isRelay
            ? (numColumns === 3
                ? 3 + NUM_LANES * 10 + 2
                : 4 + NUM_LANES * 13 + 3)
            : nonRelayHeaderHeight + NUM_LANES * nonRelayLaneHeight + nonRelayAfterHeatGap;

        const requiredSpace = eventHeaderSpace + estimatedHeatHeight;

        // Ensure the event title, headers, and first heat are not split between pages or columns
        checkPageHeight(requiredSpace);

        // Update column position after checking page height
        const columnStartX = margin + (currentColumn * (columnWidth)); // Start X position for the current column
        const columnEndX = margin + ((currentColumn + 1) * (columnWidth)); // End X position for the current column

        // Set font size and style for event title
        if (numColumns === 3) {
            doc.setFontSize(7);
            doc.setFont("MuseoSansRounded-700", "normal");
        } else {
            doc.setFontSize(10);
            doc.setFont("MuseoSansRounded-700", "normal");
        }

        const resolvedEvNum = (event.displayNumber && event.displayNumber !== String(event.number)) ? event.displayNumber : event.number
        const divSuffix = event.division ? ` [${event.division}]` : ""

        if (event.isCombined && event.combinedEventLabels && event.combinedEventLabels.length > 0) {
            const lineStep = numColumns === 3 ? 3 : 4
            const prevFontSize = numColumns === 3 ? 7 : 10
            // Draw a merge icon (two arms converging to a right-pointing arrow) before "COMBINED"
            const ic = numColumns === 3 ? 2.2 : 3.2 // icon width/height in mm
            const ix = columnStartX + 2
            const iy = yPos - ic * 0.75  // vertically center on text baseline
            const mx = ix + ic * 0.55    // x where arms meet the horizontal shaft
            const my = iy + ic * 0.5     // y midpoint (shaft)
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(numColumns === 3 ? 0.25 : 0.35)
            doc.line(ix, iy,          mx, my)         // upper arm
            doc.line(ix, iy + ic,     mx, my)         // lower arm
            doc.line(mx, my, ix + ic, my)             // horizontal shaft
            doc.line(ix + ic * 0.78, iy + ic * 0.28, ix + ic, my) // arrowhead top
            doc.line(ix + ic * 0.78, iy + ic * 0.72, ix + ic, my) // arrowhead bottom
            doc.setLineWidth(0.1)
            // "COMBINED" text offset past the icon
            doc.text("COMBINED", ix + ic + 1, yPos)
            // Lines 2..n: each event in the exact same format/style as a normal event title
            for (const label of event.combinedEventLabels) {
                yPos += lineStep
                const n = (label.displayNumber && label.displayNumber !== String(label.number)) ? label.displayNumber : label.number
                const lDivSuffix = label.division ? ` [${label.division}]` : ""
                doc.text(`#${n}${lDivSuffix} ${label.gender} ${label.ageGroup} ${label.distance}${event.course} ${label.stroke}`, columnStartX + 2, yPos)
            }
            doc.setTextColor(0, 0, 0)
            // Disclaimer for the starter
            yPos += lineStep
            doc.setFontSize(numColumns === 3 ? 5.5 : 7.5)
            doc.setFont("MuseoSansRounded-500", "normal")
            doc.setTextColor(180, 90, 0)
            doc.text("! Starter must combine these events in the Swimmingly app before racing", columnStartX + 2, yPos)
            doc.setTextColor(0, 0, 0)
            doc.setFontSize(prevFontSize)
            doc.setFont("MuseoSansRounded-700", "normal")
        } else {
            const eventName = `#${resolvedEvNum}${divSuffix} ${event.gender} ${event.ageGroup} ${event.distance}${event.course} ${event.stroke}`
            doc.text(eventName, columnStartX + 2, yPos)
        }

        yPos += 2;
        // Draw a line before the event headers, spanning the entire column width
        doc.line(columnStartX, yPos, columnEndX, yPos);
        yPos += 3;

        // Add headers for participant data
        let headers = isRelay ? ['Lane', 'Team', 'Relay', 'Seed Time'] : ['Lane', 'Name', 'Age', 'Team', 'ID', 'Seed Time'];
        let headerWidths;
        if (numColumns === 2) {
            headerWidths = isRelay ? eventHeaders2ColumnsRelay : eventHeaders2Columns; // Adjusted widths for 2 columns
        } else {
            headerWidths = isRelay ? eventHeaders3ColumnsRelay : eventHeaders3Columns; // Adjusted widths for 3 columns
        }

        let headerX = columnStartX + 2; // Adjusted to add some padding from the left
        const headerY = yPos;

        if(isRelay) {
            if (numColumns === 3) {
                doc.setFontSize(7);
                doc.setFont("MuseoSansRounded-700", "normal");
            } else {
                doc.setFontSize(10);
                doc.setFont("MuseoSansRounded-700", "normal");
            }
            // Draw relay column headers without strict column-width alignment so all
            // words display fully. "Lane" and "Team" are measured and spaced naturally;
            // a larger gap separates "Team" from "Relay"; "Seed Time" stays right-aligned.
            const wordGap = numColumns === 3 ? 2 : 3;       // gap between Lane/Team
            const relayGap = numColumns === 3 ? 4 : 6;      // larger gap between Team and Relay
            const totalRelayWidth = headerWidths.reduce((s, w) => s + w, 0); // full header row width
            const laneW = doc.getTextWidth(headers[0]);
            const teamW = doc.getTextWidth(headers[1]);
            const seedW = doc.getTextWidth(headers[3]);
            doc.text(headers[0], headerX, headerY);
            doc.text(headers[1], headerX + laneW + wordGap, headerY);
            doc.text(headers[2], headerX + laneW + wordGap + teamW + relayGap, headerY);
            // "Seed Time" right-aligned at the far right of the header row
            doc.text(headers[3], headerX + totalRelayWidth - seedW - 2, headerY);
        } else {
            headers.forEach((header, headerIdx) => {
                if (numColumns === 3) {
                    doc.setFontSize(7);
                    doc.setFont("MuseoSansRounded-700", "normal");
                } else {
                    doc.setFontSize(10);
                    doc.setFont("MuseoSansRounded-700", "normal");
                }
    
                if (headerIdx === 0 || headerIdx === 2 || headerIdx === 5) {
                    const textWidth = doc.getTextWidth(header);
                    const adjustedX = headerX + headerWidths[headerIdx] - textWidth - 2;
                    doc.text(header, adjustedX, headerY);
                    headerX += headerWidths[headerIdx];
                } else {
                    doc.text(header, headerX, headerY);
                    headerX += headerWidths[headerIdx];
                }
            });
        }
        
        

        yPos += 2;
        // Draw a line after the event headers, spanning the entire column width
        doc.line(columnStartX, yPos, columnEndX, yPos);
        yPos += 5;

        event.heats.forEach((heat, heatIndex) => {
            // Calculate required space for the heat (header + lanes) using
            // the same per-row spacing as the drawing code below so our
            // pagination check is accurate.
            const nonRelayHeaderHeightHeat = numColumns === 3 ? 3 : 4;
            const nonRelayLaneHeightHeat = numColumns === 3 ? 3 : 4;
            const nonRelayAfterHeatGapHeat = numColumns === 3 ? 2 : 3;

            const heatRequiredSpace = isRelay
                ? checkRelayHeatHeight(heat)
                : nonRelayHeaderHeightHeat + NUM_LANES * nonRelayLaneHeightHeat + nonRelayAfterHeatGapHeat;

            // Ensure the heat is not split between pages or columns
            checkPageHeight(heatRequiredSpace);

            // Update column position after checking page height
            const columnStartX = margin + (currentColumn * (columnWidth)); // Start X position for the current column
            //const columnEndX = margin + ((currentColumn + 1) * (columnWidth)); // End X position for the current column

            // Set font size and style for heat title
            if (numColumns === 3) {
                doc.setFontSize(7);
                doc.setFont("MuseoSansRounded-700", "normal");
            } else {
                doc.setFontSize(10);
                doc.setFont("MuseoSansRounded-700", "normal");
            }
            const isFinalsMeet = meet.sessionType === "finals"
            const finalsTierIndex = meet.finalsDirection === "fastestFirst"
                ? heat.number - 1
                : event.heats.length - heat.number
            const finalsTierLabel = isFinalsMeet ? (FINALS_TIER_LABELS[finalsTierIndex] ?? null) : null
            const heatHeaderText = finalsTierLabel
                ? `${finalsTierLabel} \u2022 Heat ${heat.number} of ${event.heats.length}`
                : `Heat ${heat.number} of ${event.heats.length}`
            doc.text(heatHeaderText, columnStartX + 2, yPos); // Adjusted to add some padding from the left
            if (numColumns === 3) {
                yPos += 3;
            } else {
                yPos += 4;
            }

            for (let i = 0; i < NUM_LANES; i++) {
                let headers;
                let nameLength = 15;
                if (numColumns === 2) {
                    headerWidths = isRelay ? headerWidths2ColumnsRelay : headerWidths2Columns; // Adjusted widths for 2 columns
                    nameLength = isRelay ? (headerWidths2ColumnsRelay[2]-3)/2 - 4 : headerWidths2Columns[1];
                } else {
                    headerWidths = isRelay ? headerWidths3ColumnsRelay : headerWidths3Columns; // Adjusted widths for 3 columns
                    nameLength = isRelay ? (headerWidths3ColumnsRelay[2]-3)/2 - 4 : headerWidths3Columns[1];
                }
                let headerX = columnStartX + 2; // Adjusted to add some padding from the left
                let headerY = yPos;

                // Find entrant by lane number
                let entrant = heat.entrants.find(e => e.lane === (i + 1));
                if (entrant && entrant.swimmers && entrant.swimmers.length > 0) {
                    if (isRelay) {
                        // Permutation A layout:
                        //   Row 1: Lane | Club | (blank) | Seed Time
                        //   Row 2: [Leg Name  ID] [Leg Name  ID]
                        //   Row 3: [Leg Name  ID] [Leg Name  ID]
                        const relayBaseFontSize = numColumns === 3 ? 6.2 : 9.3;
                        const relayNameShrinkFontSize = numColumns === 3 ? 5.9 : 8.9; // 1-step shrink only
                        const rowStep = numColumns === 3 ? 3 : 4;
                        const swimmerRowGap = 1; // mm gap between header row and first swimmer row

                        doc.setFontSize(relayBaseFontSize);
                        doc.setFont("MuseoSansRounded-500", "normal");

                        // Build relay swimmer objects sorted by leg position
                        const sortedSwimmers = [...entrant.swimmers].sort(
                            (a, b) => (a.swimmerOrder || 0) - (b.swimmerOrder || 0)
                        );
                        let relaySwimmers = sortedSwimmers.map((swimmer, idx) => {
                            const legPosition = swimmer.swimmerOrder || (idx + 1);
                            const legLabel = getRelayLegLabel(event.stroke, legPosition);
                            let formattedName = legLabel + ' ' + swimmer.name.split(' ').map(word => {
                                if (word === word.toLowerCase() || word === word.toUpperCase()) {
                                    return word.toLowerCase().split(' ').map(upperFirst).join(' ');
                                }
                                return word;
                            }).join(' ');
                            let id;
                            if (swimmer.number < 10) {
                                id = `00${swimmer.number}`;
                            } else if (swimmer.number < 100) {
                                id = `0${swimmer.number}`;
                            } else {
                                id = `${swimmer.number}`;
                            }
                            return { name: formattedName, id };
                        });
                        while (relaySwimmers.length < 4) {
                            relaySwimmers.push({ name: '________________', id: '' });
                        }

                        // Swimmer rows extend into the seed-time column (seed is only on row 1).
                        // This gives each slot significantly more name width than the old layout.
                        // A 5mm right-edge margin keeps the right-column IDs visually inset of the
                        // seed time value so they don't appear to share the same column.
                        const laneW = headerWidths[0];
                        const teamW = headerWidths[1];
                        const swimmerRowWidth = headerWidths[2] + headerWidths[3] - 5; // right-edge margin keeps IDs clear of seed time
                        const slotGap = 2;
                        const slotWidth = (swimmerRowWidth - slotGap) / 2;

                        // Fixed ID column width for within-slot vertical alignment across both rows
                        doc.setFontSize(relayBaseFontSize);
                        const fixedIdW = doc.getTextWidth('000');
                        const idPad = 1;
                        const nameAvailW = slotWidth - fixedIdW - idPad;

                        // Ellipsize text to fit maxWidth using real measured widths.
                        const ellipsizeFit = (text, maxWidth, fontSize) => {
                            doc.setFontSize(fontSize);
                            if (doc.getTextWidth(text) <= maxWidth) return text;
                            let t = text;
                            const ellipsisW = doc.getTextWidth('\u2026');
                            while (t.length > 0 && doc.getTextWidth(t) + ellipsisW > maxWidth) {
                                t = t.slice(0, -1);
                            }
                            return t.length > 0 ? t + '\u2026' : '\u2026';
                        };

                        // Draw one swimmer slot: name left-aligned, ID right-aligned at fixed column.
                        // Same font for all relay content; 1-step shrink before ellipsis.
                        const drawSlot = (swimmer, slotStartX, y) => {
                            doc.setFontSize(relayBaseFontSize);
                            doc.setFont("MuseoSansRounded-500", "normal");
                            if (!swimmer.id) {
                                doc.text(swimmer.name, slotStartX, y);
                                return;
                            }
                            // Try base size, then 1-step shrink, then ellipsize
                            let chosenFontSize = relayBaseFontSize;
                            if (doc.getTextWidth(swimmer.name) > nameAvailW) {
                                chosenFontSize = relayNameShrinkFontSize;
                            }
                            doc.setFontSize(chosenFontSize);
                            doc.text(ellipsizeFit(swimmer.name, nameAvailW, chosenFontSize), slotStartX, y);
                            // ID right-aligned at fixed column edge — guarantees vertical flush
                            doc.setFontSize(relayBaseFontSize);
                            doc.text(swimmer.id, slotStartX + slotWidth - doc.getTextWidth(swimmer.id), y);
                        };

                        const relayCellStartX = headerX + laneW + teamW;

                        // --- Row 1: Lane | Club | (blank middle) | Seed Time ---
                        // Lane number — right-aligned in its column (position unchanged)
                        const laneStr = `${i + 1}`;
                        doc.text(laneStr, headerX + laneW - doc.getTextWidth(laneStr) - 2, headerY);
                        // Club abbreviation — left-aligned, truncated to fit
                        let teamStr = `${entrant.swimmers[0].club}`;
                        while (doc.getTextWidth(teamStr) > teamW && teamStr.length > 0) {
                            teamStr = teamStr.slice(0, -1);
                        }
                        doc.text(teamStr, headerX + laneW, headerY);
                        // Exhibition relay: draw "ex." at the start of the blank relay area on Row 1
                        if (entrant.swimmers[0].exhibition) {
                            doc.text("ex.", relayCellStartX, headerY);
                        }
                        // Event designation for combined heats
                        if (entrant.combinedEventLabel) {
                            doc.setFontSize(numColumns === 3 ? 5.2 : 7.8)
                            doc.setTextColor(37, 99, 235)
                            const evX = relayCellStartX + (entrant.swimmers[0].exhibition ? doc.getTextWidth("ex.") + 1 : 0)
                            doc.text(`[${entrant.combinedEventLabel}]`, evX, headerY)
                            doc.setTextColor(0, 0, 0)
                            doc.setFontSize(relayBaseFontSize)
                        }
                        // Seed time — right-aligned at far right of column (position unchanged)
                        const seedStr = `${entrant.swimmers[0].seed}`;
                        const seedX = headerX + laneW + teamW + headerWidths[2] + headerWidths[3] - doc.getTextWidth(seedStr) - 2;
                        doc.text(seedStr, seedX, headerY);

                        // --- Rows 2 & 3: 2x2 swimmer matrix ---
                        const swimmerRow1Y = headerY + rowStep + swimmerRowGap;
                        const swimmerRow2Y = swimmerRow1Y + rowStep;

                        drawSlot(relaySwimmers[0], relayCellStartX, swimmerRow1Y);
                        drawSlot(relaySwimmers[1], relayCellStartX + slotWidth + slotGap, swimmerRow1Y);
                        drawSlot(relaySwimmers[2], relayCellStartX, swimmerRow2Y);
                        drawSlot(relaySwimmers[3], relayCellStartX + slotWidth + slotGap, swimmerRow2Y);
                    } else {
                        let swimmer = entrant.swimmers[0];

                        // Function to convert string to title case
                        function toTitleCase(str) {
                            return str.toLowerCase().split(' ').map(upperFirst).join(' ');
                        }

                        // Split the name into words and convert each word if it is all uppercase or all lowercase
                        let formattedName = swimmer.name.split(' ').map(word => {
                            if (word === word.toLowerCase() || word === word.toUpperCase()) {
                                return toTitleCase(word);
                            }
                            return word;
                        }).join(' ');

                         // Add "ex." if the swimmer is exhibition
                         if (swimmer.exhibition) {
                            formattedName = `ex. ${formattedName}`;
                        }

                        // Truncate the name if needed
                        let truncatedName = formattedName.length > nameLength ? formattedName.substring(0, nameLength-3) + '...' : formattedName;

                        //let truncatedName = swimmer.name.length > (headerWidths[1] - 3) ? swimmer.name.substring(0, headerWidths[1]-3) + '...' : swimmer.name;
                        let number;
                            if(swimmer.number < 10 ) {
                                number = `00${swimmer.number}`;
                            } else if (swimmer.number < 100 ) {
                                number = `0${swimmer.number}`;
                            } else {
                                number = `${swimmer.number}`;
                            }
                            if(swimmer.number < 10) {
                                //console.log(number);
                            }
                        
                        let club = swimmer.club.length > 5 ? swimmer.club.substring(0,5) : swimmer.club;

                        headers = [
                            `${i + 1}`,
                            `${truncatedName}`,
                            `${swimmer.age}`,
                            `${club}`,
                            `${number}`,
                            `${swimmer.seed}`
                        ];
                        headers.forEach((header, headerIdx) => {
                            if (numColumns === 3) {
                                doc.setFontSize(6.2);
                                doc.setFont("MuseoSansRounded-500", "normal");
                            } else {
                                doc.setFontSize(9.3);
                                doc.setFont("MuseoSansRounded-500", "normal");
                            }

                            if (headerIdx === 0 || headerIdx === 2 || headerIdx === 5) {
                                const textWidth = doc.getTextWidth(header);
                                const adjustedX = headerX + headerWidths[headerIdx] - textWidth - 2;
                                doc.text(header, adjustedX, headerY);
                            } else {
                                let truncatedHeader = header;
                                while (doc.getTextWidth(truncatedHeader) > headerWidths[headerIdx] && truncatedHeader.length > 0) {
                                    truncatedHeader = truncatedHeader.substring(0, truncatedHeader.length - 1);
                                }
                                doc.text(truncatedHeader, headerX, headerY);
                            }
                            headerX += headerWidths[headerIdx];
                        });
                    }
                } else {
                    if (isRelay) {
                        // Empty relay lane: just the lane number on a single row
                        const relayEmptyFontSize = numColumns === 3 ? 6.2 : 9.3;
                        doc.setFontSize(relayEmptyFontSize);
                        doc.setFont("MuseoSansRounded-500", "normal");
                        const laneStr = `${i + 1}`;
                        doc.text(laneStr, headerX + headerWidths[0] - doc.getTextWidth(laneStr) - 2, headerY);
                    } else {
                        headers = [`${i + 1}`, ` `, ` `, ` `, ` `, ` `];
                        headers.forEach((header, headerIdx) => {
                            if (numColumns === 3) {
                                doc.setFontSize(6.2);
                                doc.setFont("MuseoSansRounded-500", "normal");
                            } else {
                                doc.setFontSize(9.3);
                                doc.setFont("MuseoSansRounded-500", "normal");
                            }

                            if (headerIdx === 0 || headerIdx === 2 || headerIdx === 5) {
                                const textWidth = doc.getTextWidth(header);
                                const adjustedX = headerX + headerWidths[headerIdx] - textWidth - 2;
                                doc.text(header, adjustedX, headerY);
                            } else {
                                let truncatedHeader = header;
                                while (doc.getTextWidth(truncatedHeader) > headerWidths[headerIdx] && truncatedHeader.length > 0) {
                                    truncatedHeader = truncatedHeader.substring(0, truncatedHeader.length - 1);
                                }
                                doc.text(truncatedHeader, headerX, headerY);
                            }
                            headerX += headerWidths[headerIdx];
                        });
                    }
                }
                //add space after each entrant
                // For filled relay lanes: use 11/14 between lanes so gap-after-swimmers equals
                // gap-before-swimmers (x == y). Last lane keeps 10/13 so the after-heat gap is unchanged.
                const isFilledRelay = isRelay && entrant && entrant.swimmers && entrant.swimmers.length > 0;
                const isLastLane = (i === NUM_LANES - 1);
                if(numColumns === 3) {
                    yPos += isFilledRelay ? (isLastLane ? 10 : 11) : (isRelay ? 3 : 3);
                } else {
                    yPos += isFilledRelay ? (isLastLane ? 13 : 14) : (isRelay ? 4 : 4);
                }
                 // Adjust space for relays only if there are entrants
            }
            // add space after each heat
            if (numColumns === 3) {
                yPos += 2
            } else {
                yPos += 3;
            }
        });

        yPos += 1;
    });

    // Add page numbers to each page
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(8);
    doc.setFont("MuseoSansRounded-500", "normal");
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        let text = `Swimmingly® - Page ${i}/${pageCount}`;
        let xPosition = doc.internal.pageSize.getWidth() - text.length - 15;
        doc.text(text, xPosition, 8);
        drawReportLogo(
            doc,
            options.logoDataUri || null,
            options.logoWidth || null,
            options.logoHeight || null,
            doc.internal.pageSize.getWidth() - 33,
            10,
            28,
            12,
        );
    }

    const rawMeetName = meet.meetName || "Meet";
    const safeMeetName = rawMeetName.replace(/[^\w-]+/g, "_");
    const rawDate = meet.meetDate || "";
    const formattedDate = rawDate ? rawDate.replace(/\//g, "-") : "";
    const filename = formattedDate
        ? `HeatSheet_${safeMeetName}_${formattedDate}.pdf`
        : `HeatSheet_${safeMeetName}.pdf`;

    const blobUrl = doc.output("bloburl");
    if (!options || options.mode !== "previewOnly") {
        doc.save(filename);
    }

    return blobUrl;
}

export default generateHeatSheetPDF;
